// Thin cookie-authed client for team self-service endpoints. Every call sends
// credentials:'include' (the worker resolves the bw_session cookie, Phase 0).
// Returns a discriminated result so callers render specific errors.

export type ApiResult<T = Record<string, never>> = ({ ok: true } & T) | { ok: false; error: string };

async function call<T>(url: string, init: RequestInit, fetchImpl: typeof fetch): Promise<ApiResult<T>> {
  try {
    const res = await fetchImpl(url, { ...init, credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (res.ok) return { ok: true, ...(body as T) };
    return { ok: false, error: (body as { error?: string }).error ?? `http_${res.status}` };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export function joinTeam(code: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<ApiResult<{ teamId: string }>> {
  return call(`${apiBase}/api/teams/join`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) }, fetchImpl);
}

export function acceptInvite(token: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<ApiResult<{ teamId: string }>> {
  return call(`${apiBase}/api/teams/accept-invite`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token }) }, fetchImpl);
}

export function leaveTeam(teamId: string, userId: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<ApiResult> {
  return call(`${apiBase}/api/teams/${teamId}/members/${userId}`, { method: 'DELETE' }, fetchImpl);
}

export function rotateCode(teamId: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<ApiResult<{ joinCode: string }>> {
  return call(`${apiBase}/api/teams/${teamId}/rotate-code`, { method: 'POST' }, fetchImpl);
}

export function invite(teamId: string, email: string, locale: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<ApiResult> {
  return call(`${apiBase}/api/teams/${teamId}/invites`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, locale }) }, fetchImpl);
}

export type InvitePreview =
  | { status: 'valid'; teamName: string; maskedEmail: string }
  | { status: 'expired' }
  | { status: 'already_used' }
  | { status: 'invalid' }
  // 5xx / network failure — NOT a real "invalid invite". The preview endpoint
  // always answers 200 (even a bad token → 200 {status:'invalid'}), so any
  // non-2xx or thrown fetch is a transient reachability problem. Kept separate
  // so the UI can offer a retry instead of telling a visitor with a working
  // link their invitation is invalid (#5 review follow-up).
  | { status: 'transient' };

/** Read-only invite preview for the pre-login context screen. Never throws. */
export async function previewInvite(token: string, apiBase = '', fetchImpl: typeof fetch = fetch): Promise<InvitePreview> {
  try {
    const res = await fetchImpl(`${apiBase}/api/teams/invite/${encodeURIComponent(token)}`);
    if (!res.ok) return { status: 'transient' };
    return (await res.json()) as InvitePreview;
  } catch {
    return { status: 'transient' };
  }
}
